import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import DateTimePicker from 'react-datetime-picker';
import DatePicker, { registerLocale } from 'react-datepicker';
import vi from 'date-fns/locale/vi';
import { Dropdown, Button } from 'semantic-ui-react';
import Html2Canvas from 'html2canvas';
import JsPdf from 'jspdf';
import ChartCard from '../Chart/ChartCard';

import { lineOptions } from '../../utils/demo/chartsData';
import { decimalAdjust } from '../../utils/math';
import styles from './ChartReport.module.scss';
import 'react-month-picker/scss/month-picker.scss';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('vi', vi);

const ChartContent = React.memo(
  ({ isEditable, type, reports, onChangeDate, onChangeDateInterval, onFetchReports }) => {
    const [t] = useTranslation();
    const isFirstRender = useRef(true);
    const isNeedToUpdateChart = useRef(true);
    const reportsData = { ...reports };
    const { reportItems } = reportsData;
    const { energy } = reportsData;
    const local = localStorage.getItem('i18nextLng');

    console.log('********************** ChartContent render');

    let dateFilterShowFormat = 'dd/MM/yyyy';
    let dateFilterFormat = 'yMMdd';
    let datePdfFormat = 'dd/MM/yyyy';
    switch (type) {
      case 'monthly': {
        dateFilterShowFormat = 'MM/yyyy';
        dateFilterFormat = 'yMM';
        datePdfFormat = 'MM/yyyy';
        break;
      }
      case 'yearly': {
        dateFilterShowFormat = 'y';
        datePdfFormat = 'yyyy';
        dateFilterFormat = 'y';
        break;
      }
      default:
    }

    const [dateValue, setDateChange] = useState(new Date());
    const [listDevices, setListDevices] = useState([]);
    const [listItems, setListItems] = useState([]);
    const [filterItem, setFilterItem] = useState(listItems[0] ? [listItems[0].value] : []);
    const listIntervals = [
      { key: 0, value: 5, text: '5 phút' },
      { key: 1, value: 15, text: '15 phút' },
      { key: 2, value: 30, text: '30 phút' },
      { key: 3, value: 60, text: '60 phút' },
    ];
    const [filterInterval, setFilterInterval] = useState(listIntervals[3]?.value);
    const [filterDevice, setFilterDevice] = useState(listDevices[0]?.value || 0);
    const [dataValue, setDataValue] = useState();
    const [options, setOptions] = useState();
    const selectedDevice = useRef(filterDevice);
    const selectedInterval = useRef(filterInterval);
    const selectedItem = useRef([...filterItem]);
    const selectedDateValueforDayTab = useRef(dateValue);
    const selectedDateValueforMonthTab = useRef(dateValue);
    const selectedDateValueforYearTab = useRef(dateValue);

    const setListItemsForDevice = useCallback(
      (device) => {
        if (!device) return;

        const items = [];
        const selectedEnergy = energy?.find((element) => element.device.id === device);

        reportItems.forEach((item, index) => {
          const setting = selectedEnergy.settings?.find(
            (element) => element.itemName === item.itemName,
          );
          if (setting) {
            items.push({
              key: index,
              value: item.itemName,
              text: item.name,
              unit: item.unitName,
            });
          }
        });

        setListItems(items);
        const currentFilterItems = [];
        if (selectedItem.current.length > 0) {
          selectedItem.current.forEach((selectedItm) => {
            const currentFilterItm = items.find((element) => element.value === selectedItm);
            if (currentFilterItm) {
              currentFilterItems.push(currentFilterItm.value);
            }
          });
        }

        if (currentFilterItems.length > 0) {
          setFilterItem(currentFilterItems);
          selectedItem.current = currentFilterItems;
        } else {
          const wattHourItem = items.find((item) => item.value === 'watt_hour');
          let filterItems;

          if (wattHourItem) {
            filterItems = wattHourItem ? [wattHourItem.value] : [];
          } else {
            filterItems = items[0]?.value ? [items[0].value] : [];
          }

          setFilterItem(filterItems);
          selectedItem.current = filterItems;
        }
      },
      [energy, reportItems],
    );

    const getFilterDevice = useCallback(
      (event, { value }) => {
        isNeedToUpdateChart.current = true;
        setFilterDevice(value);
        selectedDevice.current = value;

        setListItemsForDevice(value);
      },
      [setListItemsForDevice],
    );

    const getFilterItem = (event, { value }) => {
      isNeedToUpdateChart.current = true;
      setFilterItem(value);
      selectedItem.current = [...value];
    };

    const getFilterInterval = (event, { value }) => {
      isNeedToUpdateChart.current = true;
      setFilterInterval(value);
      selectedInterval.current = value;
      const datevalue = format(selectedDateValueforDayTab.current, dateFilterFormat);
      onChangeDateInterval(datevalue, selectedInterval);
    };

    const onDateChange = useCallback(
      (value) => {
        isNeedToUpdateChart.current = true;
        if (value) {
          setDateChange(value);

          switch (type) {
            case 'monthly': {
              selectedDateValueforMonthTab.current = value;
              break;
            }
            case 'yearly': {
              selectedDateValueforYearTab.current = value;
              break;
            }
            default:
              selectedDateValueforDayTab.current = value;
          }

          const datevalue = format(value, dateFilterFormat);
          onChangeDate(datevalue, selectedInterval);
        }
      },
      [dateFilterFormat, onChangeDate, type],
    );

    /*
     update devices list and items list in the first render
   */
    useEffect(() => {
      if (reportItems?.length > 0) {
        if (isFirstRender.current) {
          isFirstRender.current = false;

          /* update devices list */
          const devices = [];
          energy?.forEach((itemDevice, index) => {
            devices.push({
              key: index,
              value: itemDevice.device.id,
              text: itemDevice.device.name,
            });
          });
          setListDevices(devices);
          setFilterDevice(devices[0]?.value || 0);

          /* update items list of selected device */
          setListItemsForDevice(devices[0]?.value || 0);

          isNeedToUpdateChart.current = true;
        }
      }
    }, [energy, reportItems, setListItemsForDevice]);

    useEffect(() => {
      isNeedToUpdateChart.current = true;
    }, [type]);

    useEffect(() => {
      const dataValues = energy?.find((element) => element.device.id === filterDevice);
      const yAxes = [];
      const datasets = [];

      if (selectedDevice.current) {
        setFilterDevice(selectedDevice.current);
      }

      if (selectedItem.current.length > 0) {
        setFilterItem(selectedItem.current);
      }

      // if (isNeedToUpdateChart.current === false) {
      //   return;
      // }

      if (filterItem?.length > 0) {
        filterItem.forEach((item) => {
          const reportItem = dataValues.settings.find((it) => it.itemName === item);
          let dataChart = [];
          if (dataValues) {
            dataChart = dataValues[reportItem.itemName]?.map((dt) => {
              return dt !== null ? (dt * 1).toFixed(reportItem.decimalPoint) : null;
            });
          }

          if (reportItem) {
            const checkIndex = yAxes.findIndex((y) => y.id === `y_axis_${reportItem.unitName}`);
            const maxRatio = reportItem.graphType === 'bar' ? 1.5 : 1.2;
            const settingMaxY = reportItem.maxValue ? reportItem?.maxValue * 1 : 200;
            const maxY =
              dataValues && dataValues[reportItem.itemName]
                ? Math.max(...dataValues[reportItem.itemName])
                : 0;
            const exp = maxY >= 10 ? Math.floor(Math.log10(maxY)) : 1;
            const newMaxY = decimalAdjust('ceil', (maxY + 1) * maxRatio, exp);
            const ticksMax = settingMaxY > maxY ? settingMaxY : newMaxY;

            if (checkIndex === -1) {
              yAxes.push({
                type: 'linear',
                display: 'auto',
                position: reportItem.graphType === 'bar' ? 'left' : 'right',
                id: `y_axis_${reportItem.unitName}`,
                scaleLabel: {
                  display: false,
                  labelString: reportItem.name,
                },
                ticks: {
                  fontColor: 'rgba(123, 123, 123, 0.65)',
                  fontSize: 13,
                  display: true,
                  max: ticksMax,
                  min: reportItem?.minValue || 0,
                  callback(value) {
                    return `${value} ${reportItem.unitName}`;
                  },
                  gridLines: {
                    color: 'rgba(123, 123, 123, 0.65)',
                  },
                },
              });
            } else {
              const oldMaxY = yAxes[checkIndex].ticks.max || 20;
              // eslint-disable-next-line radix
              if (checkIndex !== -1 && parseInt(oldMaxY) < parseInt(ticksMax)) {
                yAxes[checkIndex].ticks.max = ticksMax;
              }
            }

            datasets.push({
              yAxisID: `y_axis_${reportItem.unitName}`,
              type: reportItem.graphType,
              label: reportItem?.name || '',
              borderColor: reportItem.color || '#108775',
              borderWidth: 1,
              backgroundColor:
                reportItem.graphType === 'line' ? 'transparent' : reportItem.color || '#108775',
              pointBorderColor: 'transparent',
              fill: false,
              pointRadius: 3,
              pointBackgroundColor: reportItem.color || '#108775',
              data: dataChart,
              spanGaps: true,
            });
          }
        });

        isNeedToUpdateChart.current = false;
      }

      if (datasets.length > 0) {
        datasets.sort((a, b) => (a.type > b.type ? -1 : 1));
        isNeedToUpdateChart.current = false;
      }

      const dataLabels = [];
      switch (type) {
        case 'daily': {
          const dataNum = (24 * 60) / filterInterval;
          const timeVal = new Date(dateValue);
          timeVal.setHours(0);
          timeVal.setMinutes(0);
          timeVal.setSeconds(0);
          for (let i = 0; i <= dataNum - 1; i += 1) {
            const time = format(timeVal, 'HH:mm');
            dataLabels.push(time);
            timeVal.setMinutes(timeVal.getMinutes() + filterInterval);
          }
          break;
        }
        case 'monthly':
        case 'yearly': {
          for (let i = 0; i < datasets[0]?.data?.length; i += 1) {
            const label = i + 1;
            dataLabels.push(label);
          }
          break;
        }

        default:
      }

      setDataValue({
        labels: dataLabels,
        datasets,
      });

      setOptions({
        responsive: true,
        // maintainAspectRatio: false,
        scales: {
          yAxes,
          xAxes: [
            {
              ticks: {
                fontColor: 'rgba(123, 123, 123, 0.65)',
                fontSize: 13,
                display: true,
              },
              gridLines: {
                color: 'rgba(123, 123, 123, 0.65)',
              },
            },
          ],
        },
      });
    }, [energy, reportItems, filterDevice, filterItem, type, filterInterval, dateValue]);

    useEffect(() => {
      switch (type) {
        case 'monthly': {
          setDateChange(selectedDateValueforMonthTab.current);
          break;
        }
        case 'yearly': {
          setDateChange(selectedDateValueforYearTab.current);
          break;
        }
        default:
          setDateChange(selectedDateValueforDayTab.current);
      }
      onFetchReports();
    }, [onFetchReports, type]);

    const renderDateTimePicker = (chartType) => {
      if (chartType === 'daily') {
        return (
          <DateTimePicker
            className={styles.dateTimePicker}
            locale={local}
            onChange={onDateChange}
            value={dateValue}
            format={dateFilterShowFormat}
          />
        );
      }
      if (chartType === 'monthly') {
        return (
          <DatePicker
            className={styles.datePicker}
            locale={local}
            dateFormat={dateFilterShowFormat}
            showMonthYearPicker
            selected={dateValue}
            onChange={onDateChange}
          />
        );
      }
      if (chartType === 'yearly') {
        return (
          <DatePicker
            className={styles.datePicker}
            locale={local}
            dateFormat={dateFilterShowFormat}
            showYearPicker
            yearItemNumber={8}
            selected={dateValue}
            onChange={onDateChange}
          />
        );
      }

      return '';
    };

    const handleExportPdf = (e) => {
      const but = e.target;
      but.style.display = 'none';
      const graph = window.document.getElementsByClassName('divGraph')[0];

      window.devicePixelRatio = 2;

      Html2Canvas(graph, { scale: 1.6 }).then((canvas) => {
        const ctx = canvas.getContext('2d'); // get 2D context of canvas

        ctx.webkitImageSmoothingEnabled = false;
        ctx.mozImageSmoothingEnabled = false;
        ctx.msImageSmoothingEnabled = false;
        ctx.imageSmoothingEnabled = false;

        ctx.canvas.style.width = '1600px';
        ctx.canvas.style.height = '1200px';

        const imgData = canvas.toDataURL('image/png', 4.0);

        const marginLeft = 50;
        const marginRight = 24;
        const marginTop = 208;
        const marginBottom = 98;

        const pdf = new JsPdf({
          orientation: 'l', // landscape
          unit: 'pt', // points
          format: 'a4', // set needed dimensions for any element
          // filters: ['ASCIIHexEncode'],
        });

        const width = pdf.internal.pageSize.getWidth();
        const height = pdf.internal.pageSize.getHeight();
        const canvasWidth = width - marginLeft - marginRight;
        const canvasHeight = height - marginTop - marginBottom;

        pdf.addFont('/Roboto/Roboto-Light.ttf', 'Roboto', 'normal');
        pdf.addFont('/Roboto/Roboto-Medium.ttf', 'Roboto-Medium', 'normal');
        pdf.addFont('/Roboto/Roboto-Regular.ttf', 'Roboto-Regular', 'normal');
        pdf.addFont('/Roboto/Roboto-LightItalic.ttf', 'Roboto-Italic', 'normal');

        pdf.setFont('Roboto');
        pdf.setFontSize(28);

        let textString;
        let textWidth;

        ctx.textBaseline = 'top'; // start with drawing text from top
        ctx.font = '28px Roboto'; // font and size

        /* header - 1st line: TITLE */
        textString = `${t('common.menus.charts')} ${t(`common.reportType.${type}`)} - ${format(
          dateValue,
          datePdfFormat,
        )}`;
        textWidth = ctx.measureText(textString).width;
        pdf.text(width / 2 - textWidth / 2, 75, textString);

        /* header - 2nd line: DEVICE */
        textString = `${t('common.components.devices')}: `;
        if (filterDevice?.length > 0) {
          const listDevice = listDevices.find((it) => it.value === filterDevice);
          textString += `${listDevice.text}`;
        }
        pdf.setFontSize(16);
        ctx.font = '16px Roboto'; // font and size
        textWidth = ctx.measureText(textString).width;
        pdf.text(width / 2 - textWidth / 2, 110, textString);

        /* header - 3rd line: ITEMS */
        textString = `${t('common.item')}: `;
        if (filterItem?.length > 0) {
          filterItem.forEach((item, idx, array) => {
            const listItem = listItems.find((it) => it.value === item);
            textString += `${listItem.text} `;

            if (idx !== array.length - 1) {
              textString += `, `;
            }
          });
        }

        pdf.setFontSize(16);
        textWidth = ctx.measureText(textString).width;
        pdf.text(width / 2 - textWidth / 2, 145, textString);

        pdf.addImage(imgData, 'png', marginLeft, marginTop, canvasWidth, canvasHeight);
        pdf.save(
          `${t('common.menus.charts')} ${t(`common.reportType.${type}`)} - ${format(
            dateValue,
            dateFilterFormat,
          )}.pdf`,
        );
        but.style.display = 'inline-block';
      });
    };

    return (
      <>
        {listItems.length > 0 && (
          <div className={styles.reportFilter}>
            {listDevices.length > 0 && (
              <Dropdown
                className={styles.filterItems}
                value={filterDevice || listDevices[0]?.value || 0}
                search
                selection
                options={listDevices}
                onChange={getFilterDevice}
              />
            )}
            <Dropdown
              className={styles.filterItems}
              value={filterItem || listItems[0]?.value || 0}
              search
              selection
              multiple
              options={listItems}
              onChange={getFilterItem}
            />
            {renderDateTimePicker(type)}
            {type === 'daily' && (
              <Dropdown
                className={styles.interval}
                defaultValue={filterInterval}
                search
                selection
                options={listIntervals}
                onChange={getFilterInterval}
              />
            )}
            {isEditable && filterItem.length > 0 && (
              <div className={styles.actionExport}>
                <Button onClick={handleExportPdf}>{t('action.exportGraphToPdf')}</Button>
              </div>
            )}
          </div>
        )}
        <div className={styles.tableWrapper}>
          {dataValue && options && filterDevice !== 0 && filterItem.length > 0 && (
            <ChartCard className="divGraph" title="">
              <Bar
                height={95}
                data={dataValue}
                options={options}
                legend={lineOptions.legend}
                redraw
              />
            </ChartCard>
          )}
        </div>
      </>
    );
  },
);

ChartContent.propTypes = {
  isEditable: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  reports: PropTypes.objectOf(PropTypes.any).isRequired,
  onChangeDate: PropTypes.func.isRequired,
  onChangeDateInterval: PropTypes.func.isRequired,
  onFetchReports: PropTypes.func.isRequired,
};

export default ChartContent;
